'use client';

import React, { useMemo } from 'react';
import { useWorkspaceRouter } from '@/app/features/workspace/routing/useWorkspaceRouter';
import { useSelector } from 'react-redux';
import { Loader2, ArrowRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import type { RootState } from '@/app/store';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import {
  selectActiveBatch,
  selectActiveBatchPercent,
} from '@/app/store/selectors/packAnalysisWorkbenchSelectors';

/**
 * Spec: pack-analysis-ingest-background-progress Reqs 2.1-2.6.
 *
 * Persistent ingest-progress chip in the AppShell header. Renders nothing
 * when no batch is active (Req 2.2 — zero footprint at rest). Click opens a
 * Radix popover anchored to the badge with summary fields + a "Open Ingest
 * tab →" link (Req 2.3 / 2.4).
 *
 * Reads slice state directly so route-level pages don't need to plumb props.
 */
export const IngestProgressBadge = React.memo(function IngestProgressBadge() {
  const activeBatch = useSelector(selectActiveBatch);
  const percent = useSelector(selectActiveBatchPercent);
  const activeTenant = useSelector((s: RootState) => s.tenant.activeTenant);
  const router = useWorkspaceRouter();
  const { t } = useTranslation('packAnalysisWorkbench');

  const elapsedLabel = useMemo(() => {
    if (activeBatch == null) return '';
    const startedAt = new Date(activeBatch.startedAt).getTime();
    if (Number.isNaN(startedAt)) return '';
    const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }, [activeBatch]);

  if (activeBatch == null) return null;

  const totalFiles = activeBatch.totalFiles;
  const completedFiles = activeBatch.completedFiles;
  const failedFiles = activeBatch.failedFiles;

  const handleOpenIngestTab = () => {
    router.push(`${workspaceHref(activeTenant, 'apps/pack-analysis')}?tab=ingest`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 text-primary hover:text-primary"
          aria-label={t('ingestProgress.badge.ariaLabel', { count: 1 })}
          title={t('ingestProgress.badge.ariaLabel', { count: 1 })}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="sr-only">
            {t('ingestProgress.badge.ariaLabel', { count: 1 })}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 overflow-hidden"
      >
        <div className="px-4 pt-3 pb-2 border-b">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">
              {t('ingestProgress.popover.title')}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">
              {elapsedLabel}
            </div>
          </div>
        </div>
        <div className="px-4 py-3 space-y-3">
          {/* Inline progress bar — no shared <Progress/> primitive in this app
             yet; keeping it self-contained avoids a sibling-component spec. */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">
                {t('ingestProgress.popover.fileCounts', {
                  completed: completedFiles,
                  total: totalFiles,
                  failed: failedFiles,
                })}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {percent != null ? `${percent}%` : '—'}
              </span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-300"
                style={{ width: `${percent ?? 0}%` }}
              />
            </div>
          </div>
          {activeBatch.currentFileName ? (
            <div className="text-xs text-muted-foreground truncate" title={activeBatch.currentFileName}>
              <span className="font-medium text-foreground">
                {t('ingestProgress.popover.currentFile')}:
              </span>{' '}
              {activeBatch.currentFileName}
            </div>
          ) : null}
          {failedFiles > 0 ? (
            <div className="text-xs text-destructive">
              {t('ingestProgress.popover.failedSoFar', { failed: failedFiles })}
            </div>
          ) : null}
        </div>
        <div className="px-4 py-2 border-t bg-muted/30">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleOpenIngestTab}
            className="w-full justify-between text-xs"
          >
            {t('ingestProgress.popover.openIngestTab')}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
});
IngestProgressBadge.displayName = 'IngestProgressBadge';
